import * as Sentry from '@sentry/vue'

/**
 * A deploy prunes the previous release's hashed chunks, so a session holding the old entry
 * 404s on lazy imports (vite dispatches `vite:preloadError` on dynamic-import/preload failure).
 * Reloading fetches the current entry and its chunks — but when the asset is *actually* gone
 * (bad deploy, CDN miss), the reloaded page 404s on the same chunk and reloads again, pinning
 * the browser at 100% CPU and hammering the CDN.
 *
 * So the reload is a one-shot: the first failure stamps sessionStorage and reloads; a second
 * failure while that stamp is fresh shows the message below and reports to Sentry instead.
 */
export const ASSET_RELOAD_STAMP_KEY = 'osello:asset-reload-attempted-at'

export const ASSET_LOAD_FAILURE_MESSAGE =
    'Refresh failed, please reload your page or contact our team for help.'

/**
 * How long a reload stamp counts as "this failure episode". A pure one-shot would spend the
 * session's only reload on the first deploy — every later deploy (SPA tabs live for days) would
 * then show the error banner instead of recovering silently. Past the window it's a new episode
 * and the reload is allowed again, which still bounds us to one reload per minute, not a loop.
 */
const EPISODE_MS = 60_000

const ERROR_ELEMENT_ATTRIBUTE = 'data-asset-load-error'

/** sessionStorage throws outright in Safari private mode — a dead stamp must not break the guard. */
function readReloadStamp(): number | null {
    try {
        const stamp = window.sessionStorage.getItem(ASSET_RELOAD_STAMP_KEY)
        return stamp === null ? null : Number(stamp)
    } catch {
        return null
    }
}

function writeReloadStamp(): void {
    try {
        window.sessionStorage.setItem(ASSET_RELOAD_STAMP_KEY, String(Date.now()))
    } catch {
        // No stamp means the next failure reloads again — still bounded, since a browser that
        // can't write sessionStorage can't have loaded a chunk from cache either.
    }
}

function showFailureMessage(): void {
    if (document.querySelector(`[${ERROR_ELEMENT_ATTRIBUTE}]`)) return

    const banner = document.createElement('div')
    banner.setAttribute(ERROR_ELEMENT_ATTRIBUTE, '')
    banner.setAttribute('role', 'alert')
    // Inline styles, not Tailwind: the failed asset may well be the stylesheet.
    banner.style.cssText = [
        'position:fixed', 'inset-inline:1rem auto', 'bottom:1rem', 'z-index:2147483647',
        'max-width:24rem', 'padding:0.75rem 1rem', 'border-radius:0.5rem',
        'background:#fef2f2', 'border:1px solid #fecaca', 'color:#991b1b',
        'font:500 0.875rem/1.4 system-ui,sans-serif', 'box-shadow:0 4px 12px rgba(0,0,0,0.15)',
    ].join(';')
    banner.textContent = ASSET_LOAD_FAILURE_MESSAGE
    document.body.appendChild(banner)
}

/**
 * Registers the `vite:preloadError` handler. The event is left un-prevented so vite still
 * rethrows — the in-flight navigation fails loudly rather than resolving to an undefined
 * component.
 */
export function installAssetReloadGuard(): void {
    window.addEventListener('vite:preloadError', (event) => {
        const stamp = readReloadStamp()
        const reloadedRecently = stamp !== null && Date.now() - stamp < EPISODE_MS

        if (reloadedRecently) {
            Sentry.captureException(event.payload, {
                tags: {asset_load_failure: 'reload_did_not_recover'},
                extra: {url: window.location.href, reloaded_at: stamp},
            })
            showFailureMessage()
            return
        }

        writeReloadStamp()
        window.location.reload()
    })
}
